import { getApiUrl } from "../api";
import { useEffect, useMemo, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";

const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const splitList = (value) => String(value || "")
  .replace(/<br\s*\/?>/gi, "\n")
  .replace(/<\/p>/gi, "\n")
  .replace(/<li[^>]*>/gi, "\n")
  .replace(/<[^>]*>/g, " ")
  .split(/\r?\n|[•|]/)
  .map((item) => item.replace(/\s+/g, " ").trim())
  .filter(Boolean);

const mapCategory = (item, index, language = "en") => {
  return {
    id: item.id || String(index + 1),
    title: stripHtml(language === "ar" ? (item.title_ar || item.title_en || item.title) : (item.title_en || item.title)),
    icon: getCloudflareImageUrl(item.icon || item.ImagePath, "w=120,sharpen=1"),
    position: Number(item.position) || index + 1,
  };
};

const mapService = (item, index, language = "en") => ({
  id: item.id || `service-${index}`,
  catid: item.catid || "",
  subcatid: item.id || "",
  categoryTitle: stripHtml(language === "ar" ? (item.category_title_ar || item.category_title_en || item.categoryTitle) : item.category_title_en) || "",
  title: stripHtml(language === "ar" ? (item.title_ar || item.title_en || item.title) : (item.title_en || item.title)),
  description: language === "ar" ? (item.aidrequestdescription_ar || item.aidrequestbannerdescription_ar || item.aidrequestserviceduration_ar || item.aidrequestdescription_en || "") : (item.aidrequestdescription_en || item.aidrequestbannerdescription_en || item.aidrequestserviceduration_en || ""),
  duration: stripHtml(language === "ar" ? (item.aidrequestserviceduration_ar || item.aidrequestserviceduration_en) : item.aidrequestserviceduration_en),
  audience: stripHtml(language === "ar" ? (item.aidrequesttargetaudiance_ar || item.aidrequesttargetaudiance_en) : item.aidrequesttargetaudiance_en),
  emirates: stripHtml(language === "ar" ? (item.aidrequestemirates_ar || item.aidrequestemirates_en) : item.aidrequestemirates_en),
  icon: getCloudflareImageUrl(item.ImagePath, "w=120,sharpen=1"),
  banner: getCloudflareImageUrl(item.aidrequestbanner, "w=900,sharpen=1"),
  position: Number(item.position) || index + 1,
});

const mapRequiredDocument = (item, language = "en") => ({
  id: item.id || "",
  catid: item.catid || "",
  subcatid: item.subcatid || "",
  title: stripHtml(language === "ar" ? (item.title_ar || item.title_en || item.title) : (item.title_en || item.title)) || "",
  position: Number(item.position) || 0,
});

const iconForCategory = (title) => {
  const lower = String(title || "").toLowerCase();
  if (lower.includes("health") || lower.includes("medical") || lower.includes("صح") || lower.includes("طب")) return "fa-solid fa-briefcase-medical";
  if (lower.includes("education") || lower.includes("school") || lower.includes("university") || lower.includes("تعليم") || lower.includes("مدرس")) return "fa-solid fa-graduation-cap";
  if (lower.includes("housing") || lower.includes("rental") || lower.includes("سكن") || lower.includes("إيجار")) return "fa-solid fa-house";
  if (lower.includes("executive") || lower.includes("case") || lower.includes("تنفيذ") || lower.includes("حالة")) return "fa-solid fa-shield-halved";
  return "fa-solid fa-list-check";
};

export default function Services({ language = "en" }) {
  const interactions = useHomeInteractions();
  const isArabic = language === "ar";
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [requiredDocumentsByService, setRequiredDocumentsByService] = useState({});
  const [activeCategoryId, setActiveCategoryId] = useState("all");
  const [message, setMessage] = useState("");

  const visibleGroups = useMemo(() => {
    const selectedCategories = activeCategoryId === "all"
      ? categories
      : categories.filter((category) => String(category.id) === String(activeCategoryId));

    return selectedCategories.map((category) => ({
      ...category,
      services: services
        .filter((service) => String(service.catid) === String(category.id))
        .sort((left, right) => left.position - right.position),
    })).filter((category) => category.services.length);
  }, [activeCategoryId, categories, services]);

  useEffect(() => {
    document.title = isArabic ? "الخدمات | مؤسسة تراحم الخيرية" : "Services | Tarahum Charity Foundation";
  }, [isArabic]);

  useEffect(() => {
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => { document.body.classList.remove("arabic-layout-page"); document.documentElement.lang = "en"; document.documentElement.dir = "ltr"; };
  }, [isArabic]);

  useEffect(() => {
    const controller = new AbortController();

    const loadServices = async () => {
      try {
        const categoryResponse = await fetch(getApiUrl("/api/GetDonationCategoryList.ashx?typ=Donation&pageSize=100"), {
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: controller.signal,
        });

        if (!categoryResponse.ok) throw new Error(`Category request failed with ${categoryResponse.status}`);
        const categoryResult = await categoryResponse.json();
        const apiCategories = (Array.isArray(categoryResult?.data) ? categoryResult.data : [])
          .map((item, index) => mapCategory(item, index, language))
          .filter((category) => category.title);

        const nextCategories = apiCategories;
        setCategories(nextCategories);

        const serviceResponses = await Promise.all(nextCategories.map((category) => (
          fetch(getApiUrl(`/api/GetDonationSubCategoryList.ashx?catid=${encodeURIComponent(category.id)}&pageSize=100`), {
            headers: { Accept: "application/json" },
            credentials: "include",
            signal: controller.signal,
          })
        )));

        const serviceResults = await Promise.all(serviceResponses.map((response) => {
          if (!response.ok) throw new Error(`Subcategory request failed with ${response.status}`);
          return response.json();
        }));

        const apiServices = serviceResults
          .flatMap((result) => Array.isArray(result?.data) ? result.data : [])
          .map((item, index) => mapService(item, index, language))
          .filter((service) => service.title);

        if (apiServices.length) {
          setServices(apiServices);
        }

        const requiredDocResponses = await Promise.all(nextCategories.map((category) => (
          fetch(getApiUrl(`/api/GetRequiredDocByCategoryList.ashx?catid=${encodeURIComponent(category.id)}&pageSize=500`), {
            headers: { Accept: "application/json" },
            credentials: "include",
            signal: controller.signal,
          })
        )));

        const requiredDocResults = await Promise.all(requiredDocResponses.map((response) => {
          if (!response.ok) throw new Error(`Required document request failed with ${response.status}`);
          return response.json();
        }));

        const groupedRequiredDocuments = {};
        requiredDocResults
          .flatMap((result) => Array.isArray(result?.data) ? result.data : [])
          .map((item) => mapRequiredDocument(item, language))
          .filter((document) => document.title)
          .forEach((document) => {
            const key = `${document.catid}:${document.subcatid}`;
            if (!groupedRequiredDocuments[key]) groupedRequiredDocuments[key] = [];
            groupedRequiredDocuments[key].push(document);
          });

        Object.keys(groupedRequiredDocuments).forEach((key) => {
          groupedRequiredDocuments[key].sort((left, right) => left.position - right.position);
        });
        setRequiredDocumentsByService(groupedRequiredDocuments);

        setMessage("");
      } catch (error) {
        if (error.name !== "AbortError") {
          setMessage(isArabic ? "تعذر تحميل بيانات الخدمات حالياً." : "Unable to load services right now.");
          console.warn("Unable to load services", error);
        }
      }
    };

    loadServices();
    return () => controller.abort();
  }, [isArabic, language]);

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <h1>{isArabic ? "الخدمات" : "Services"}</h1>
            <ul className="kid-breadcrumb">
              <li><Link to="/">{isArabic ? "الرئيسية" : "home"}</Link></li>
              <li>/</li>
              <li>{isArabic ? "الخدمات" : "Services"}</li>
            </ul>
          </div>
        </div>
      </section>

      <div className="services-section">
        <div className="container">
          <div className="tabs-container">
            <button
              className={`tab-label ${activeCategoryId === "all" ? "active" : ""}`}
              type="button"
              onClick={() => setActiveCategoryId("all")}
            >
              <i className="fa-solid fa-bars"></i>
              {isArabic ? "جميع الخدمات" : "All Services"}
            </button>
            {categories.map((category) => (
              <button
                className={`tab-label ${String(activeCategoryId) === String(category.id) ? "active" : ""}`}
                type="button"
                key={category.id}
                onClick={() => setActiveCategoryId(category.id)}
              >
                <i className={iconForCategory(category.title)}></i>
                {category.title}
              </button>
            ))}
          </div>

          {message ? (
            <div className="in-kind-alert" role="alert">
              <span className="in-kind-alert-icon">
                <i className="fa-solid fa-circle-info"></i>
              </span>
              <span>
                <strong>{isArabic ? "تنبيه المحتوى" : "Content notice"}</strong>
                <small>{message}</small>
              </span>
            </div>
          ) : null}

          <div className="content-area">
            <div className="tab-content content-all" style={{ display: "block" }}>
              {visibleGroups.map((category) => (
                <section className="services-category-group" key={category.id}>
                  <div className="category-header" style={{ marginTop: 0 }}>
                    <div className="category-icon">
                      <i className={iconForCategory(category.title)}></i>
                    </div>
                    <h2 className="category-title">{category.title}</h2>
                  </div>

                  <div className="services-grid">
                    {category.services.map((service) => {
                      const requiredDocuments = requiredDocumentsByService[`${service.catid}:${service.subcatid}`] || [];
                      const requirements = requiredDocuments.length
                        ? requiredDocuments.map((document) => document.title)
                        : splitList(service.description);
                      return (
                        <div className="services-new-card" key={service.id}>
                          <Link to={`/service-details?catid=${encodeURIComponent(service.catid)}&subcatid=${encodeURIComponent(service.subcatid || service.id)}&id=${encodeURIComponent(service.id)}`}>
                            <div className="service-card-header">
                              <div className="service-icon">
                                {service.icon ? <img src={service.icon} alt="" /> : <i className={iconForCategory(category.title)}></i>}
                              </div>
                              <h3 className="service-name">{service.title}</h3>
                            </div>

                            <p className="requirements-title">{isArabic ? "تفاصيل الخدمة" : "Service Details"}</p>
                            {requirements.length ? (
                              <ul>
                                {requirements.slice(0, 4).map((requirement) => (
                                  <li key={requirement}>{requirement}</li>
                                ))}
                              </ul>
                            ) : (
                              <ul>
                                {service.duration ? <li>{service.duration}</li> : null}
                                {service.audience ? <li>{service.audience}</li> : null}
                                {service.emirates ? <li>{service.emirates}</li> : null}
                                {!service.duration && !service.audience && !service.emirates ? <li>{isArabic ? "عرض تفاصيل الخدمة والمتطلبات." : "View service details and requirements."}</li> : null}
                              </ul>
                            )}
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
